import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { 
  Users, 
  FileText, 
  Send,
  LogOut, 
  Menu, 
  X,
  Download,
  CheckCircle,
  Clock,
  UserPlus,
  Trash2,
  BarChart3,
  FolderOpen,
  ChevronRight,
  Calendar,
  Edit3,
  Save,
  AlertCircle,
  Pause
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    'Active': { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    'On Hold': { color: 'bg-amber-100 text-amber-700', icon: Pause },
    'Review Needed': { color: 'bg-red-100 text-red-700', icon: AlertCircle }
  };
  
  const config = statusConfig[status] || statusConfig['Active'];
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium ${config.color}`}>
      <Icon size={12} strokeWidth={2} />
      {status}
    </span>
  );
};

// Sidebar Navigation
const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { path: '/accountant', icon: Users, label: 'Clients', exact: true },
    { path: '/accountant/all-documents', icon: FileText, label: 'All Documents' },
    { path: '/accountant/send', icon: Send, label: 'Send Document' },
    { path: '/accountant/whitelist', icon: UserPlus, label: 'Whitelist' }
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-obsidian/50 z-40 lg:hidden" 
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-obsidian z-50 transform transition-transform duration-200 ease-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b border-graphite">
            <div className="flex items-center justify-between">
              <Link to="/" className="font-fraunces font-bold text-lg text-parchment">
                Oakmere
              </Link>
              <button onClick={onClose} className="lg:hidden text-iron-grey hover:text-parchment">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>
            <p className="font-inter text-xs text-iron-grey mt-1">Accountant Dashboard</p>
          </div>
          
          {/* User Info */}
          <div className="p-4 border-b border-graphite">
            <p className="font-inter text-sm font-medium text-parchment truncate">{user?.name}</p>
            <p className="font-inter text-xs text-iron-grey truncate">{user?.email}</p>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-4">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={onClose}
                    className={`flex items-center gap-3 px-4 py-3 font-inter text-sm transition-colors ${
                      isActive(item.path, item.exact)
                        ? 'bg-parchment text-obsidian'
                        : 'text-parchment hover:bg-graphite'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <item.icon size={18} strokeWidth={1.5} />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          
          {/* Logout */}
          <div className="p-4 border-t border-graphite">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full font-inter text-sm text-iron-grey hover:text-parchment hover:bg-graphite transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={18} strokeWidth={1.5} />
              Sign Out
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Documents Page (All Client Documents)
const DocumentsPage = () => {
  const { getAuthHeader } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accountant/documents`, {
        headers: getAuthHeader()
      });
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/accountant/documents/${doc.id}/download`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleMarkProcessed = async (doc) => {
    try {
      await axios.post(`${API}/accountant/documents/${doc.id}/mark-processed`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Document marked as processed');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to update document status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-inter text-iron-grey">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-fraunces font-bold text-2xl text-obsidian mb-6">Client Documents</h1>
      
      {documents.length === 0 ? (
        <div className="bg-white border border-iron-grey p-8 text-center card-shadow">
          <FileText size={48} className="mx-auto mb-4 text-iron-grey" strokeWidth={1} />
          <p className="font-inter text-obsidian mb-2">No documents yet</p>
          <p className="font-inter text-sm text-iron-grey">
            Client uploads will appear here
          </p>
        </div>
      ) : (
        <div className="bg-white border border-iron-grey card-shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="accountant-documents-table">
              <thead>
                <tr className="border-b border-iron-grey bg-parchment">
                  <th className="px-4 py-3 text-left font-inter text-xs uppercase tracking-wider text-iron-grey">Document</th>
                  <th className="px-4 py-3 text-left font-inter text-xs uppercase tracking-wider text-iron-grey">Client</th>
                  <th className="px-4 py-3 text-left font-inter text-xs uppercase tracking-wider text-iron-grey">Type</th>
                  <th className="px-4 py-3 text-left font-inter text-xs uppercase tracking-wider text-iron-grey">Size</th>
                  <th className="px-4 py-3 text-left font-inter text-xs uppercase tracking-wider text-iron-grey">Uploaded</th>
                  <th className="px-4 py-3 text-left font-inter text-xs uppercase tracking-wider text-iron-grey">Status</th>
                  <th className="px-4 py-3 text-left font-inter text-xs uppercase tracking-wider text-iron-grey">Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.id} className="border-b border-iron-grey last:border-0 hover:bg-parchment/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <FileText size={18} className="text-iron-grey" strokeWidth={1.5} />
                        <span className="font-inter text-sm text-obsidian truncate max-w-[150px]">
                          {doc.original_filename}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-inter text-sm text-obsidian">
                        {doc.uploaded_by_name}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-inter text-sm text-iron-grey capitalize">
                        {doc.document_type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-inter text-sm text-iron-grey">
                        {formatFileSize(doc.file_size)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-inter text-xs text-iron-grey">
                        {formatDate(doc.uploaded_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {doc.status === 'processed' ? (
                          <CheckCircle size={16} className="text-green-600" strokeWidth={1.5} />
                        ) : (
                          <Clock size={16} className="text-iron-grey" strokeWidth={1.5} />
                        )}
                        <span className="font-inter text-sm capitalize">
                          {doc.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handleDownload(doc)}
                          variant="outline"
                          size="sm"
                          className="border-obsidian text-obsidian hover:bg-obsidian hover:text-parchment"
                          data-testid={`download-${doc.id}`}
                        >
                          <Download size={14} strokeWidth={1.5} />
                        </Button>
                        {doc.status !== 'processed' && (
                          <Button
                            onClick={() => handleMarkProcessed(doc)}
                            variant="outline"
                            size="sm"
                            className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                            data-testid={`mark-processed-${doc.id}`}
                          >
                            <CheckCircle size={14} strokeWidth={1.5} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Clients Page (Organized View)
const ClientsPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accountant/clients-organized`, {
        headers: getAuthHeader()
      });
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-inter text-iron-grey">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-fraunces font-bold text-2xl text-obsidian mb-2">Clients</h1>
      <p className="font-inter text-sm text-iron-grey mb-6">
        Click on a client to view their documents organized by time period
      </p>
      
      {clients.length === 0 ? (
        <div className="bg-white border border-iron-grey p-8 text-center card-shadow">
          <Users size={48} className="mx-auto mb-4 text-iron-grey" strokeWidth={1} />
          <p className="font-inter text-obsidian mb-2">No clients yet</p>
          <p className="font-inter text-sm text-iron-grey">
            Registered clients will appear here
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((item) => (
            <div 
              key={item.client.id} 
              className="bg-white border border-iron-grey p-6 card-shadow card-shadow-hover cursor-pointer"
              onClick={() => navigate(`/accountant/client/${item.client.id}`)}
              data-testid={`client-card-${item.client.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-obsidian flex items-center justify-center">
                    <span className="font-fraunces font-bold text-xl text-parchment">
                      {item.client.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-inter text-sm font-medium text-obsidian">{item.client.name}</p>
                    <p className="font-inter text-xs text-iron-grey">{item.client.email}</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-iron-grey" strokeWidth={1.5} />
              </div>
              
              {/* Assignment Info */}
              {item.assignment && (
                <div className="mb-4 p-3 bg-parchment border-l-2 border-obsidian">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-inter text-xs text-iron-grey">Assigned to:</span>
                    <StatusBadge status={item.assignment.status} />
                  </div>
                  <p className="font-inter text-sm font-medium text-obsidian">
                    {item.assignment.assigned_accountant || 'Unassigned'}
                  </p>
                </div>
              )}
              
              {/* Documents Count */}
              <div className="flex items-center justify-between pt-4 border-t border-iron-grey">
                <div className="flex items-center gap-2">
                  <FolderOpen size={16} className="text-iron-grey" strokeWidth={1.5} />
                  <span className="font-inter text-sm text-obsidian">
                    {item.total_documents} document{item.total_documents !== 1 ? 's' : ''}
                  </span>
                </div>
                <span className="font-inter text-xs text-iron-grey">
                  {Object.keys(item.documents_by_period).length} period{Object.keys(item.documents_by_period).length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Client Detail Page
const ClientDetailPage = () => {
  const { client_id } = useParams();
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingAssignment, setEditingAssignment] = useState(false);
  const [assignment, setAssignment] = useState({
    assigned_accountant: '',
    notes: '',
    status: 'Active'
  });
  const [saving, setSaving] = useState(false);

  const fetchClientData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accountant/client/${client_id}`, {
        headers: getAuthHeader()
      });
      setClientData(response.data);
      if (response.data.assignment) {
        setAssignment({
          assigned_accountant: response.data.assignment.assigned_accountant || '',
          notes: response.data.assignment.notes || '',
          status: response.data.assignment.status || 'Active'
        });
      }
    } catch (error) {
      toast.error('Failed to load client data');
      navigate('/accountant');
    } finally {
      setLoading(false);
    }
  }, [client_id, getAuthHeader, navigate]);

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/accountant/documents/${doc.id}/download`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleMarkProcessed = async (doc) => {
    try {
      await axios.post(`${API}/accountant/documents/${doc.id}/mark-processed`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Document marked as processed');
      fetchClientData();
    } catch (error) {
      toast.error('Failed to update document status');
    }
  };

  const handleSaveAssignment = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/accountant/client/${client_id}/assignment`, assignment, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      toast.success('Client assignment updated');
      setEditingAssignment(false);
      fetchClientData();
    } catch (error) {
      toast.error('Failed to update assignment');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-inter text-iron-grey">Loading...</p>
      </div>
    );
  }

  if (!clientData) {
    return null;
  }

  return (
    <div>
      {/* Back Button & Header */}
      <button 
        onClick={() => navigate('/accountant')}
        className="flex items-center gap-2 font-inter text-sm text-iron-grey hover:text-obsidian transition-colors mb-4"
        data-testid="back-to-clients"
      >
        <ChevronRight size={16} strokeWidth={1.5} className="rotate-180" />
        Back to Clients
      </button>

      <div className="flex flex-col lg:flex-row gap-6 mb-8">
        {/* Client Info Card */}
        <div className="bg-white border border-iron-grey p-6 card-shadow flex-1">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-obsidian flex items-center justify-center">
              <span className="font-fraunces font-bold text-2xl text-parchment">
                {clientData.client.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="font-fraunces font-bold text-2xl text-obsidian">{clientData.client.name}</h1>
              <p className="font-inter text-sm text-iron-grey">{clientData.client.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="font-inter text-iron-grey">
              <FolderOpen size={14} className="inline mr-1" strokeWidth={1.5} />
              {clientData.total_documents} documents
            </span>
            <span className="font-inter text-iron-grey">
              <Calendar size={14} className="inline mr-1" strokeWidth={1.5} />
              Joined {new Date(clientData.client.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
            </span>
          </div>
        </div>

        {/* Assignment Card */}
        <div className="bg-white border border-iron-grey p-6 card-shadow lg:w-96">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-fraunces font-bold text-lg text-obsidian">Assignment</h2>
            {!editingAssignment ? (
              <Button
                onClick={() => setEditingAssignment(true)}
                variant="ghost"
                size="sm"
                className="text-iron-grey hover:text-obsidian"
                data-testid="edit-assignment-btn"
              >
                <Edit3 size={16} strokeWidth={1.5} />
              </Button>
            ) : (
              <Button
                onClick={handleSaveAssignment}
                disabled={saving}
                size="sm"
                className="btn-primary text-xs py-1"
                data-testid="save-assignment-btn"
              >
                <Save size={14} strokeWidth={1.5} className="mr-1" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
          </div>

          {editingAssignment ? (
            <div className="space-y-4">
              <div>
                <label className="font-inter text-xs text-iron-grey mb-1 block">Assigned Accountant</label>
                <Input
                  placeholder="Enter accountant name"
                  value={assignment.assigned_accountant}
                  onChange={(e) => setAssignment({ ...assignment, assigned_accountant: e.target.value })}
                  className="bg-parchment border-iron-grey text-obsidian"
                  data-testid="assignment-accountant"
                />
              </div>
              <div>
                <label className="font-inter text-xs text-iron-grey mb-1 block">Status</label>
                <Select 
                  value={assignment.status} 
                  onValueChange={(value) => setAssignment({ ...assignment, status: value })}
                >
                  <SelectTrigger className="bg-parchment border-iron-grey" data-testid="assignment-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-iron-grey">
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="On Hold">On Hold</SelectItem>
                    <SelectItem value="Review Needed">Review Needed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-inter text-xs text-iron-grey mb-1 block">Notes</label>
                <Textarea
                  placeholder="Add notes about this client..."
                  value={assignment.notes}
                  onChange={(e) => setAssignment({ ...assignment, notes: e.target.value })}
                  rows={3}
                  className="bg-parchment border-iron-grey text-obsidian placeholder:text-iron-grey resize-none"
                  data-testid="assignment-notes"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <span className="font-inter text-xs text-iron-grey">Accountant</span>
                <p className="font-inter text-sm font-medium text-obsidian">
                  {clientData.assignment?.assigned_accountant || 'Not assigned'}
                </p>
              </div>
              <div>
                <span className="font-inter text-xs text-iron-grey">Status</span>
                <div className="mt-1">
                  <StatusBadge status={clientData.assignment?.status || 'Active'} />
                </div>
              </div>
              {clientData.assignment?.notes && (
                <div>
                  <span className="font-inter text-xs text-iron-grey">Notes</span>
                  <p className="font-inter text-sm text-graphite mt-1 p-2 bg-parchment border-l-2 border-iron-grey">
                    {clientData.assignment.notes}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Documents by Period */}
      <h2 className="font-fraunces font-bold text-xl text-obsidian mb-4">Documents</h2>
      
      {Object.keys(clientData.documents_by_period).length === 0 ? (
        <div className="bg-white border border-iron-grey p-8 text-center card-shadow">
          <FileText size={48} className="mx-auto mb-4 text-iron-grey" strokeWidth={1} />
          <p className="font-inter text-obsidian mb-2">No documents uploaded yet</p>
          <p className="font-inter text-sm text-iron-grey">
            Documents will appear here when the client uploads them
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(clientData.documents_by_period).map(([period, docs]) => (
            <div key={period} className="bg-white border border-iron-grey card-shadow">
              <div className="px-4 py-3 bg-parchment border-b border-iron-grey flex items-center gap-2">
                <Calendar size={16} className="text-obsidian" strokeWidth={1.5} />
                <h3 className="font-fraunces font-bold text-lg text-obsidian">{period}</h3>
                <span className="font-inter text-xs text-iron-grey ml-2">
                  ({docs.length} document{docs.length !== 1 ? 's' : ''})
                </span>
              </div>
              <div className="divide-y divide-iron-grey">
                {docs.map((doc) => (
                  <div 
                    key={doc.id} 
                    className="px-4 py-3 flex items-center justify-between hover:bg-parchment/50 transition-colors"
                    data-testid={`doc-${doc.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText size={18} className="text-iron-grey flex-shrink-0" strokeWidth={1.5} />
                      <div className="min-w-0">
                        <p className="font-inter text-sm text-obsidian truncate">{doc.original_filename}</p>
                        <p className="font-inter text-xs text-iron-grey">
                          {formatFileSize(doc.file_size)} • {doc.document_type.replace(/_/g, ' ')} • {formatDate(doc.uploaded_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="flex items-center gap-1">
                        {doc.status === 'processed' ? (
                          <CheckCircle size={14} className="text-emerald-600" strokeWidth={2} />
                        ) : (
                          <Clock size={14} className="text-iron-grey" strokeWidth={2} />
                        )}
                        <span className="font-inter text-xs capitalize">{doc.status}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handleDownload(doc)}
                          variant="outline"
                          size="sm"
                          className="border-obsidian text-obsidian hover:bg-obsidian hover:text-parchment h-8 w-8 p-0"
                          data-testid={`download-${doc.id}`}
                        >
                          <Download size={14} strokeWidth={1.5} />
                        </Button>
                        {doc.status !== 'processed' && (
                          <Button
                            onClick={() => handleMarkProcessed(doc)}
                            variant="outline"
                            size="sm"
                            className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white h-8 w-8 p-0"
                            data-testid={`mark-processed-${doc.id}`}
                          >
                            <CheckCircle size={14} strokeWidth={1.5} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Send Document Page
const SendDocumentPage = () => {
  const { getAuthHeader } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedClient, setSelectedClient] = useState('');
  const [message, setMessage] = useState('');
  const [isFinancialDocument, setIsFinancialDocument] = useState(true);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accountant/clients`, {
        headers: getAuthHeader()
      });
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSend = async () => {
    if (!selectedFile || !selectedClient) {
      toast.error('Please select a file and client');
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('client_id', selectedClient);
      formData.append('is_financial_document', isFinancialDocument);
      if (message) {
        formData.append('message', message);
      }

      await axios.post(`${API}/accountant/send-document`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Document sent successfully${isFinancialDocument ? ' - AI analysis queued' : ''}`);
      setSelectedFile(null);
      setSelectedClient('');
      setMessage('');
      setIsFinancialDocument(true);
      const fileInput = document.getElementById('send-file-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      toast.error('Failed to send document');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-inter text-iron-grey">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-fraunces font-bold text-2xl text-obsidian mb-6">Send Document to Client</h1>
      
      <div className="bg-white border border-iron-grey p-6 card-shadow max-w-xl">
        <p className="font-inter text-sm text-iron-grey mb-6">
          Send processed documents back to clients. Financial documents (P&L, Balance Sheets) will be automatically analyzed by AI to populate their dashboard.
        </p>
        
        {/* Client Select */}
        <div className="mb-6">
          <label className="font-inter text-sm text-obsidian mb-2 block">Select Client</label>
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger className="bg-parchment border-iron-grey" data-testid="client-select">
              <SelectValue placeholder="Choose a client" />
            </SelectTrigger>
            <SelectContent className="bg-white border-iron-grey">
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name} ({client.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* File Drop Zone */}
        <div className="mb-6">
          <label className="font-inter text-sm text-obsidian mb-2 block">Document</label>
          <div className="border-2 border-dashed border-iron-grey p-6 text-center hover:border-obsidian transition-colors">
            <input
              type="file"
              id="send-file-input"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="send-file-input"
            />
            <label 
              htmlFor="send-file-input" 
              className="cursor-pointer"
            >
              <Send size={24} className="mx-auto mb-3 text-iron-grey" strokeWidth={1.5} />
              <p className="font-inter text-sm text-obsidian">
                {selectedFile ? selectedFile.name : 'Click to select a file'}
              </p>
            </label>
          </div>
        </div>

        {/* Financial Document Checkbox */}
        <div className="mb-6 flex items-center gap-3 p-4 bg-parchment border border-iron-grey">
          <Checkbox 
            id="financial-doc"
            checked={isFinancialDocument}
            onCheckedChange={setIsFinancialDocument}
            data-testid="financial-document-checkbox"
          />
          <div>
            <label 
              htmlFor="financial-doc" 
              className="font-inter text-sm font-medium text-obsidian cursor-pointer"
            >
              Financial Document (P&L / Balance Sheet)
            </label>
            <p className="font-inter text-xs text-iron-grey mt-1">
              <BarChart3 size={12} className="inline mr-1" />
              AI will extract metrics for the client's dashboard
            </p>
          </div>
        </div>
        
        {/* Message */}
        <div className="mb-6">
          <label className="font-inter text-sm text-obsidian mb-2 block">Message (optional)</label>
          <Textarea 
            placeholder="Add a note for the client..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            className="bg-parchment border-iron-grey text-obsidian placeholder:text-iron-grey resize-none"
            data-testid="send-message"
          />
        </div>
        
        {/* Send Button */}
        <Button 
          onClick={handleSend}
          disabled={!selectedFile || !selectedClient || sending}
          className="btn-primary w-full"
          data-testid="send-btn"
        >
          {sending ? 'Sending...' : 'Send Document'}
        </Button>
      </div>
    </div>
  );
};

// Whitelist Management Page
const WhitelistPage = () => {
  const { getAuthHeader } = useAuth();
  const [whitelist, setWhitelist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchWhitelist = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/whitelist`, {
        headers: getAuthHeader()
      });
      setWhitelist(response.data);
    } catch (error) {
      toast.error('Failed to load whitelist');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchWhitelist();
  }, [fetchWhitelist]);

  const handleAdd = async () => {
    if (!newEmail) {
      toast.error('Please enter an email');
      return;
    }

    setAdding(true);
    try {
      await axios.post(`${API}/admin/whitelist?email=${encodeURIComponent(newEmail)}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Email added to whitelist');
      setModalOpen(false);
      setNewEmail('');
      fetchWhitelist();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add email';
      toast.error(message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (id) => {
    try {
      await axios.delete(`${API}/admin/whitelist/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('Removed from whitelist');
      fetchWhitelist();
    } catch (error) {
      toast.error('Failed to remove from whitelist');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-inter text-iron-grey">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-fraunces font-bold text-2xl text-obsidian">Accountant Whitelist</h1>
          <p className="font-inter text-sm text-iron-grey mt-1">
            Users with these emails will register as accountants
          </p>
        </div>
        <Button 
          onClick={() => setModalOpen(true)}
          className="btn-primary"
          data-testid="add-whitelist-btn"
        >
          <UserPlus size={16} strokeWidth={1.5} className="mr-2" />
          Add Email
        </Button>
      </div>
      
      {whitelist.length === 0 ? (
        <div className="bg-white border border-iron-grey p-8 text-center card-shadow">
          <UserPlus size={48} className="mx-auto mb-4 text-iron-grey" strokeWidth={1} />
          <p className="font-inter text-obsidian mb-2">No emails whitelisted</p>
          <p className="font-inter text-sm text-iron-grey">
            Add emails to allow accountant registrations
          </p>
        </div>
      ) : (
        <div className="bg-white border border-iron-grey card-shadow">
          {whitelist.map((item, i) => (
            <div 
              key={item.id} 
              className={`flex items-center justify-between p-4 ${i < whitelist.length - 1 ? 'border-b border-iron-grey' : ''}`}
              data-testid={`whitelist-${item.id}`}
            >
              <div>
                <p className="font-inter text-sm text-obsidian">{item.email}</p>
                <p className="font-inter text-xs text-iron-grey">Added {formatDate(item.added_at)}</p>
              </div>
              <Button
                onClick={() => handleRemove(item.id)}
                variant="ghost"
                size="sm"
                className="text-iron-grey hover:text-red-600"
                data-testid={`remove-${item.id}`}
              >
                <Trash2 size={16} strokeWidth={1.5} />
              </Button>
            </div>
          ))}
        </div>
      )}
      
      {/* Add Email Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-parchment border-iron-grey">
          <DialogHeader>
            <DialogTitle className="font-fraunces font-bold text-xl text-obsidian">
              Add to Whitelist
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <Input 
              type="email"
              placeholder="accountant@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="bg-white border-iron-grey text-obsidian placeholder:text-iron-grey"
              data-testid="whitelist-email-input"
            />
            <Button 
              onClick={handleAdd}
              disabled={adding}
              className="btn-primary w-full mt-4"
              data-testid="confirm-add-btn"
            >
              {adding ? 'Adding...' : 'Add Email'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Accountant Dashboard Component
const AccountantDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-parchment">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white border-b border-iron-grey px-4 py-4 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-obsidian"
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} strokeWidth={1.5} />
            </button>
            <div className="lg:hidden font-fraunces font-bold text-lg text-obsidian">
              Accountant
            </div>
            <div className="w-6 lg:hidden" />
          </div>
        </header>
        
        {/* Page Content */}
        <main className="p-4 lg:p-8" data-testid="accountant-dashboard-content">
          <Routes>
            <Route index element={<ClientsPage />} />
            <Route path="client/:client_id" element={<ClientDetailPage />} />
            <Route path="all-documents" element={<DocumentsPage />} />
            <Route path="send" element={<SendDocumentPage />} />
            <Route path="whitelist" element={<WhitelistPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AccountantDashboard;
